// Consolidated national + per-district turnout history.
//
// ONE interactive chart, 2003-2026, that replaces the prior split
// between the national-only ElectionDay/TurnoutHistoryChart and the
// per-district TurnoutDistrictsChart. Behaviour:
//
//   · Wave toggle (2pm / Closing) at the top right — drives every
//     line on the chart simultaneously.
//   · National average always rendered as a thick dashed line so
//     the comparative anchor is permanent.
//   · 13 district lines, each colored by 2022 margin of victory
//     (via marginToColor — same palette as the cubes + maps).
//   · Per-district legend chips below the chart, click to toggle
//     visibility. Quick-select buttons above the chips: All / Clear
//     / PL strongholds / PN strongholds / Marginals.
//   · Recharts <Tooltip> shows the year + every visible series'
//     value on hover. Active dots highlight the hovered series.
//   · Recharts <Brush> at the bottom gives a draggable
//     stock-app-style zoom over the year range — the user can pull
//     either handle in to focus on, say, 2017→2026.
//
// Data sources:
//   · 2pm wave   → src/data/turnoutHistory.ts (TURNOUT_HISTORY +
//                  NATIONAL_TURNOUT_HISTORY pct_2pm fields).
//   · close wave → src/data/district_turnout_history.json
//                  (canonical per-district closing figures across
//                  2003-2022) + NATIONAL_TURNOUT_HISTORY pct_close.
//                  2017 national close is null (Commission only
//                  released 2pm that year) — the dashed line
//                  breaks at 2017 in close mode, by design.
//   · 2026       → useElectionDayTurnout (pct_2pm / pct_10pm) —
//                  the live editor-entered figures.
//
// Honesty rule: a null reading becomes `null` in the data series
// rather than 0; Recharts skips it and the line breaks cleanly.

import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MAP_DATA, marginToColor } from '../../lib/buildMapPaths';
import {
  TURNOUT_HISTORY,
  NATIONAL_TURNOUT_HISTORY,
  type ElectionYear,
} from '../../data/turnoutHistory';
import { useElectionDayTurnout } from '../../lib/useElectionDayTurnout';
import historyJson from '../../data/district_turnout_history.json';
import FeatureSection from '../FeatureSection';
import TurnoutShareButton from '../Share/TurnoutShareButton';
import './TurnoutHistoryUnified.css';

const CLOSE_HISTORY = historyJson as Record<string, Record<string, number>>;
const PAST_YEARS: ElectionYear[] = [2003, 2008, 2013, 2017, 2022];
const ALL_YEARS = [...PAST_YEARS, 2026] as const;

type Wave = 'pct_2pm' | 'pct_close';

interface DistrictMeta {
  id: number;
  color: string;
  margin: number;
}

/** 13 districts sorted by 2022 margin (most-PN first, most-PL last)
 *  — so the legend reads left-to-right blue → red, matching the
 *  map's perceptual palette. */
const DISTRICTS: DistrictMeta[] = MAP_DATA.paths
  .map((p) => ({
    id: p.id,
    color: marginToColor(p.properties.results_2022.margin_pp),
    margin: p.properties.results_2022.margin_pp,
  }))
  .sort((a, b) => a.margin - b.margin);

// Stronghold / marginal thresholds for the preset quick-selects.
// |margin| ≥ 10pp = stronghold; |margin| < 5pp = marginal. Picked
// after a glance at the 2022 figures — broad enough for at least 3
// districts per preset so the chart stays informative on each one.
const STRONGHOLD_THRESHOLD = 10;
const MARGINAL_THRESHOLD = 5;

const PL_STRONGHOLDS = DISTRICTS.filter((d) => d.margin >= STRONGHOLD_THRESHOLD).map((d) => d.id);
const PN_STRONGHOLDS = DISTRICTS.filter((d) => d.margin <= -STRONGHOLD_THRESHOLD).map((d) => d.id);
const MARGINALS = DISTRICTS.filter((d) => Math.abs(d.margin) < MARGINAL_THRESHOLD).map((d) => d.id);
const ALL_IDS = DISTRICTS.map((d) => d.id);

/** Lookup one (year, district, wave) cell with the source-fallback
 *  policy used across the table + chart. */
function readDistrict(
  districtId: number,
  year: ElectionYear,
  wave: Wave,
): number | null {
  if (wave === 'pct_2pm') {
    const v = TURNOUT_HISTORY[year]?.[districtId]?.pct_2pm;
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
  }
  const fromJson = CLOSE_HISTORY[`District ${districtId}`]?.[String(year)];
  if (typeof fromJson === 'number' && Number.isFinite(fromJson)) return fromJson;
  const fromHist = TURNOUT_HISTORY[year]?.[districtId]?.pct_close;
  return typeof fromHist === 'number' && Number.isFinite(fromHist) ? fromHist : null;
}

function readNational(year: ElectionYear, wave: Wave): number | null {
  const rec = NATIONAL_TURNOUT_HISTORY[year];
  const v = wave === 'pct_2pm' ? rec.pct_2pm : rec.pct_close;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

interface ChartRow {
  year: number;
  national: number | null;
  // Per-district fields keyed `d1`, `d2`, …, `d13`.
  [districtField: `d${number}`]: number | null;
}

export default function TurnoutHistoryChart() {
  // Wave defaults to the latest 2026 reading available — closing the
  // moment any district has a 10pm figure, else 2pm. Once the user
  // clicks a tab manually, their choice is pinned (userPinnedRef) so
  // a late-arriving 10pm reading doesn't yank their 2pm view away.
  const [wave, setWave] = useState<Wave>('pct_2pm');
  const userPinnedRef = useRef(false);
  const [visible, setVisible] = useState<Set<number>>(() => new Set(ALL_IDS));
  const [hoveredDistrict, setHoveredDistrict] = useState<number | null>(null);
  // Brush window — tracks which slice of years the user has zoomed to.
  // Defaults to the full range. With XAxis type="number" + explicit
  // domain, Recharts' Brush doesn't auto-rescale the axis, so we wire
  // the indices into state and drive XAxis domain/ticks off them. The
  // SELECTED range fills the chart's full width.
  const [brushRange, setBrushRange] = useState<[number, number]>([0, ALL_YEARS.length - 1]);
  const { data: turnout } = useElectionDayTurnout();
  useEffect(() => {
    if (userPinnedRef.current) return;
    const has10pm = Object.values(turnout.districts ?? {}).some(
      (d) => d?.pct_10pm !== null && d?.pct_10pm !== undefined,
    );
    setWave(has10pm ? 'pct_close' : 'pct_2pm');
  }, [turnout]);
  function handleWaveTab(next: Wave): void {
    userPinnedRef.current = true;
    setWave(next);
  }

  // Build the data array — one row per year. 2026 cells read from
  // the live editor state.
  const data = useMemo<ChartRow[]>(() => {
    return ALL_YEARS.map((year) => {
      const row = { year } as ChartRow;
      if (year === 2026) {
        row.national =
          wave === 'pct_close'
            ? turnout.national.pct_10pm
            : turnout.national.pct_2pm;
        for (const d of DISTRICTS) {
          const live = turnout.districts[`District ${d.id}`];
          const v =
            wave === 'pct_close'
              ? (live?.pct_10pm ?? null)
              : (live?.pct_2pm ?? null);
          (row as unknown as Record<string, number | null>)[`d${d.id}`] = v;
        }
      } else {
        row.national = readNational(year as ElectionYear, wave);
        for (const d of DISTRICTS) {
          (row as unknown as Record<string, number | null>)[`d${d.id}`] = readDistrict(d.id, year as ElectionYear, wave);
        }
      }
      return row;
    });
  }, [wave, turnout]);

  // Y-domain — wave-specific, keeps the inter-district spread legible.
  const yDomain: [number, number] = wave === 'pct_close' ? [78, 100] : [30, 65];
  const yTicks = wave === 'pct_close'
    ? [80, 85, 90, 95, 100]
    : [35, 40, 45, 50, 55, 60, 65];

  function setOnlyVisible(ids: number[]): void {
    setVisible(new Set(ids));
  }
  function toggleDistrict(id: number): void {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const visibleDistricts = DISTRICTS.filter((d) => visible.has(d.id));

  // Derived axis bounds from the brush window. Clamp to data length so
  // a stale brushRange (after a future data-shape change) can't index
  // past the end.
  const [startIdx, endIdx] = brushRange;
  const safeStart = Math.max(0, Math.min(startIdx, data.length - 1));
  const safeEnd = Math.max(safeStart, Math.min(endIdx, data.length - 1));
  const zoomDomain: [number, number] = [data[safeStart].year, data[safeEnd].year];
  const zoomTicks = data.slice(safeStart, safeEnd + 1).map((r) => r.year);

  return (
    <div className="turnout-history-chart">
      <header className="turnout-history-chart__head">
        <div className="turnout-history-chart__title-row">
          <div>
            <h3 className="turnout-history-chart__title">
              National & district turnout · 2003–2026
            </h3>
          </div>
          <div
            className="turnout-history-chart__wave-toggle"
            role="tablist"
            aria-label="Reading"
          >
            <button
              type="button"
              role="tab"
              aria-selected={wave === 'pct_2pm'}
              className={`turnout-history-chart__wave-tab${wave === 'pct_2pm' ? ' is-active' : ''}`}
              onClick={() => handleWaveTab('pct_2pm')}
            >
              2pm
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={wave === 'pct_close'}
              className={`turnout-history-chart__wave-tab${wave === 'pct_close' ? ' is-active' : ''}`}
              onClick={() => handleWaveTab('pct_close')}
            >
              Closing
            </button>
          </div>
          {/* Share-as-PNG affordance — flag-gated by `share_buttons`.
              Sits next to the wave toggle so the chart's interactive
              chrome stays in one row. */}
          <FeatureSection sectionKey="share_buttons" label="Share buttons">
            <TurnoutShareButton label="Share chart" variant="chip" />
          </FeatureSection>
        </div>

        <div className="turnout-history-chart__presets">
          <button
            type="button"
            className="turnout-history-chart__preset"
            onClick={() => setOnlyVisible(ALL_IDS)}
          >
            All districts
          </button>
          <button
            type="button"
            className="turnout-history-chart__preset"
            onClick={() => setOnlyVisible([])}
          >
            Clear
          </button>
          <button
            type="button"
            className="turnout-history-chart__preset is-pl"
            onClick={() => setOnlyVisible(PL_STRONGHOLDS)}
          >
            PL strongholds
          </button>
          <button
            type="button"
            className="turnout-history-chart__preset is-pn"
            onClick={() => setOnlyVisible(PN_STRONGHOLDS)}
          >
            PN strongholds
          </button>
          <button
            type="button"
            className="turnout-history-chart__preset"
            onClick={() => setOnlyVisible(MARGINALS)}
          >
            Marginals
          </button>
        </div>
      </header>

      <div className="turnout-history-chart__chart">
        <ResponsiveContainer width="100%" height={420}>
          <LineChart
            data={data}
            margin={{ top: 16, right: 24, bottom: 8, left: 8 }}
          >
            <CartesianGrid stroke="#e8e8e8" strokeDasharray="3 3" />
            <XAxis
              dataKey="year"
              type="number"
              domain={zoomDomain}
              ticks={zoomTicks}
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={{ stroke: '#999' }}
              axisLine={{ stroke: '#999' }}
              allowDuplicatedCategory={false}
            />
            <YAxis
              domain={yDomain}
              ticks={yTicks}
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={{ stroke: '#999' }}
              axisLine={{ stroke: '#999' }}
              tickFormatter={(v: number) => `${v}%`}
              width={48}
            />
            <ReferenceLine
              x={2026}
              stroke="#cf1b2b"
              strokeDasharray="2 4"
              ifOverflow="hidden"
            />
            <Tooltip
              cursor={{ stroke: '#999', strokeWidth: 1 }}
              content={
                <TurnoutTooltip
                  visibleDistricts={visibleDistricts}
                  hoveredDistrict={hoveredDistrict}
                />
              }
            />
            <Legend
              verticalAlign="top"
              height={1}
              wrapperStyle={{ display: 'none' }}
            />

            {/* National average — always on, always dashed gray. */}
            <Line
              type="monotone"
              dataKey="national"
              name="National average"
              stroke="#222"
              strokeWidth={2.5}
              strokeDasharray="6 4"
              dot={{ r: 3.5, fill: '#222', stroke: '#222' }}
              activeDot={{ r: 5 }}
              connectNulls={false}
              isAnimationActive={false}
            />

            {/* District lines — visible-controlled. */}
            {visibleDistricts.map((d) => (
              <Line
                key={d.id}
                type="monotone"
                dataKey={`d${d.id}`}
                name={`District ${d.id}`}
                stroke={d.color}
                strokeWidth={hoveredDistrict === d.id ? 3 : 1.75}
                strokeOpacity={
                  hoveredDistrict !== null && hoveredDistrict !== d.id ? 0.25 : 0.9
                }
                dot={false}
                activeDot={{ r: 5, fill: d.color, stroke: d.color }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}

            <Brush
              dataKey="year"
              height={28}
              stroke="#999"
              fill="#f3f3f3"
              travellerWidth={8}
              startIndex={safeStart}
              endIndex={safeEnd}
              onChange={(range) => {
                const s = (range as { startIndex?: number } | undefined)?.startIndex;
                const e = (range as { endIndex?: number } | undefined)?.endIndex;
                if (typeof s === 'number' && typeof e === 'number' && s !== e) {
                  setBrushRange([s, e]);
                }
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ── District legend (click-to-toggle) ─────────────────── */}
      <div
        className="turnout-history-chart__legend"
        role="group"
        aria-label="Districts shown"
      >
        {DISTRICTS.map((d) => {
          const on = visible.has(d.id);
          return (
            <button
              key={d.id}
              type="button"
              className={`turnout-history-chart__chip${on ? ' is-on' : ''}`}
              style={{ ['--chip-color' as string]: d.color }}
              onClick={() => toggleDistrict(d.id)}
              onMouseEnter={() => setHoveredDistrict(d.id)}
              onMouseLeave={() => setHoveredDistrict(null)}
              aria-pressed={on}
            >
              <span className="turnout-history-chart__chip-swatch" aria-hidden="true" />
              <span className="turnout-history-chart__chip-label">D{d.id}</span>
            </button>
          );
        })}
      </div>

    </div>
  );
}

// ── Custom tooltip ─────────────────────────────────────────────────

interface TooltipPayloadEntry {
  dataKey?: string;
  value?: number | null;
  color?: string;
  payload?: ChartRow;
}

interface TooltipProps {
  active?: boolean;
  label?: number;
  payload?: TooltipPayloadEntry[];
  visibleDistricts: DistrictMeta[];
  hoveredDistrict: number | null;
}

function TurnoutTooltip({
  active,
  label,
  payload,
  visibleDistricts,
  hoveredDistrict,
}: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  const nat = payload.find((p) => p.dataKey === 'national');
  const districts = visibleDistricts
    .map((d) => {
      const entry = payload.find((p) => p.dataKey === `d${d.id}`);
      return entry?.value != null
        ? { id: d.id, color: d.color, value: entry.value as number }
        : null;
    })
    .filter((x): x is { id: number; color: string; value: number } => x !== null)
    // Highest turnout first within the tooltip — most useful ordering.
    .sort((a, b) => b.value - a.value);

  // If the user is hovering a chip, surface only that district +
  // national in the tip — cuts the visual noise.
  const districtRows = hoveredDistrict !== null
    ? districts.filter((d) => d.id === hoveredDistrict)
    : districts;

  return (
    <div className="turnout-history-chart__tooltip">
      <div className="turnout-history-chart__tooltip-year">{label}</div>
      {nat?.value != null && (
        <div className="turnout-history-chart__tooltip-row is-national">
          <span className="turnout-history-chart__tooltip-swatch is-dashed" />
          <span className="turnout-history-chart__tooltip-name">National</span>
          <span className="turnout-history-chart__tooltip-value">
            {(nat.value as number).toFixed(1)}%
          </span>
        </div>
      )}
      {districtRows.length === 0 ? (
        <div className="turnout-history-chart__tooltip-empty">
          No districts selected
        </div>
      ) : (
        districtRows.slice(0, 8).map((d) => (
          <div key={d.id} className="turnout-history-chart__tooltip-row">
            <span
              className="turnout-history-chart__tooltip-swatch"
              style={{ background: d.color }}
            />
            <span className="turnout-history-chart__tooltip-name">D{d.id}</span>
            <span className="turnout-history-chart__tooltip-value">
              {d.value.toFixed(1)}%
            </span>
          </div>
        ))
      )}
      {districtRows.length > 8 && (
        <div className="turnout-history-chart__tooltip-more">
          + {districtRows.length - 8} more
        </div>
      )}
    </div>
  );
}
